import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtTime, timeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { RefreshCw, Copy, Check, Radio } from "lucide-react";

export default function RawPayloadPanel() {
  const [copied, setCopied] = useState(false);
  const { data = [], isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ["raw-payloads"],
    queryFn: async () => (await api.get("/raw-payloads", { params: { limit: 5 } })).data,
    refetchInterval: 15000,
  });

  const latest = data[0];

  const copyBody = () => {
    if (!latest) return;
    const text = latest.raw_body || JSON.stringify(latest.payload, null, 2);
    navigator.clipboard.writeText(text).then(
      () => { setCopied(true); toast.success("Raw body copied"); setTimeout(() => setCopied(false), 1500); },
      () => toast.error("Copy failed — select the text manually")
    );
  };

  return (
    <div className="bg-card border border-border" data-testid="raw-payload-panel">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-green-500" />
          <div>
            <p className="overline">raw ingest inspector · debug</p>
            <h3 className="text-sm font-medium mt-1">Exact stored request body from the last incoming ingest</h3>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-muted-foreground">
            checked {dataUpdatedAt ? timeAgo(new Date(dataUpdatedAt).toISOString()) : "—"}
          </span>
          <Button variant="ghost" size="sm" onClick={() => refetch()} data-testid="refresh-raw-button" className="rounded-none font-mono text-xs hover:bg-secondary gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {!latest ? (
        <div className="px-5 py-10 text-center font-mono text-xs text-muted-foreground" data-testid="raw-empty">
          No ingest received yet. Once your laptop sends OTLP data, the exact stored body appears here.
        </div>
      ) : (
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border font-mono text-xs">
            {[
              ["endpoint", `/v1/${latest.endpoint_type}`],
              ["received", fmtTime(latest.received_at)],
              ["source ip", latest.source_ip || "—"],
              ["token", latest.source_token_label || "—"],
              ["records", latest.record_count ?? "—"],
              ["parse error", latest.parse_error ? "yes" : "no"],
            ].map(([k, v]) => (
              <div key={k} className="bg-card p-3">
                <p className="overline">{k}</p>
                <p className={`mt-1 truncate ${k === "parse error" && latest.parse_error ? "text-red-500" : "text-white"}`}>{String(v)}</p>
              </div>
            ))}
          </div>

          {latest.request_headers && (
            <div>
              <p className="overline mb-1">request headers</p>
              <pre className="text-[11px] text-zinc-300 bg-black border border-border p-3 overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(latest.request_headers, null, 2)}</pre>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="overline">raw request body (exact bytes received)</p>
              <button onClick={copyBody} data-testid="copy-raw-body" className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground hover:text-white transition-colors">
                {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />} {copied ? "copied" : "copy"}
              </button>
            </div>
            {!latest.raw_body && (
              <p className="font-mono text-[11px] text-amber-500/80 mb-1" data-testid="raw-legacy-note">
                exact bytes not stored for this older payload — showing the reformatted parsed JSON
              </p>
            )}
            <pre data-testid="raw-body-content" className="text-[11px] text-zinc-300 bg-black border border-border p-4 overflow-auto max-h-[420px] whitespace-pre-wrap break-all">{latest.raw_body || JSON.stringify(latest.payload, null, 2)}</pre>
          </div>

          {data.length > 1 && (
            <p className="font-mono text-[11px] text-muted-foreground/70">
              Showing the most recent of {data.length} recent payloads (metrics + logs).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
